/**
 * Knowledge Control queue — Phase 11 admin/governance.
 *
 * Reviewer-facing Knowledge Base stays read-only. This page is the human
 * approval queue for DRAFT / TESTING / APPROVED rules.
 */

import { FormEvent, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  api,
  KnowledgeControlListItem,
  KnowledgeControlStats,
} from '../lib/api'
import {
  KnowledgeControlAuthError,
  clearKnowledgeAdminToken,
  loadKnowledgeAdminToken,
  resolveKnowledgeAdminAccess,
  saveKnowledgeAdminToken,
} from '../lib/knowledgeControlAuth'
import { LoadingSpinner } from '../components/design-system/Loading'
import { EmptyState } from '../components/design-system/EmptyState'
import { Badge } from '../components/design-system/Badge'
import { Button } from '../components/design-system/Button'
import { useLocale } from '../contexts/LocaleContext'
import { actionLabel, formatLabel, lifecycleLabel, severityLabel, validationLabel } from '../lib/i18n'

const STATUS_VALUES = ['', 'draft', 'testing', 'approved', 'active', 'deprecated', 'rejected']

function statusTone(status: string): 'warning' | 'info' | 'success' | 'danger' | 'default' {
  if (status === 'draft' || status === 'testing') return 'warning'
  if (status === 'approved' || status === 'active') return 'success'
  if (status === 'rejected') return 'danger'
  if (status === 'deprecated') return 'default'
  return 'info'
}

export function KnowledgeControlPage() {
  const { t, locale } = useLocale()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [tokenDraft, setTokenDraft] = useState('')
  const [hasToken, setHasToken] = useState<boolean | null>(() => (loadKnowledgeAdminToken() ? true : null))
  const [unauthorized, setUnauthorized] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [items, setItems] = useState<KnowledgeControlListItem[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [stats, setStats] = useState<KnowledgeControlStats | null>(null)

  const search = searchParams.get('search') || ''
  const status = searchParams.get('status') || ''
  const page = parseInt(searchParams.get('page') || '1', 10)
  const pageSize = 20

  useEffect(() => {
    if (hasToken !== null) return
    let cancelled = false
    void resolveKnowledgeAdminAccess(() => api.me()).then((ok) => {
      if (!cancelled) setHasToken(ok)
    })
    return () => {
      cancelled = true
    }
  }, [hasToken])

  useEffect(() => {
    if (hasToken !== true) {
      if (hasToken === false) {
        setLoading(false)
        setUnauthorized(true)
      }
      return
    }
    loadQueue()
  }, [hasToken, search, status, page])

  async function loadQueue() {
    try {
      setLoading(true)
      setError(null)
      setUnauthorized(false)
      const listParams: Parameters<typeof api.listKnowledgeControlRules>[0] = {
        search_query: search || undefined,
        page,
        page_size: pageSize,
      }
      if (status && status !== 'all') {
        listParams.status = status
      }
      const [listData, statsData] = await Promise.all([
        api.listKnowledgeControlRules(listParams),
        api.getKnowledgeControlStats(),
      ])
      setItems(listData.items)
      setTotalCount(listData.total_count)
      setTotalPages(listData.total_pages)
      setStats(statsData)
    } catch (err) {
      if (err instanceof KnowledgeControlAuthError) {
        setUnauthorized(true)
        setItems([])
        setStats(null)
      } else {
        setError(err instanceof Error ? err.message : t('kc.failed'))
      }
    } finally {
      setLoading(false)
    }
  }

  function updateFilter(key: string, value: string) {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    next.delete('page')
    setSearchParams(next)
  }

  function goToPage(nextPage: number) {
    const next = new URLSearchParams(searchParams)
    next.set('page', String(nextPage))
    setSearchParams(next)
  }

  function handleSaveToken(event: FormEvent) {
    event.preventDefault()
    if (!tokenDraft.trim()) return
    saveKnowledgeAdminToken(tokenDraft)
    setTokenDraft('')
    setHasToken(true)
    setUnauthorized(false)
  }

  function handleClearToken() {
    clearKnowledgeAdminToken()
    setHasToken(false)
    setUnauthorized(true)
    setItems([])
    setStats(null)
  }

  if (hasToken === null) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <LoadingSpinner size="lg" text={t('common.signingIn')} />
      </div>
    )
  }

  if (unauthorized || !hasToken) {
    return (
      <div style={{ maxWidth: '520px', margin: '0 auto', padding: '32px' }} data-testid="knowledge-control-token-form">
        <h1
          style={{
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-2xl)',
            marginBottom: '12px',
          }}
        >
          {t('nav.knowledgeControl')}
        </h1>
        <p
          style={{
            fontFamily: 'var(--reviewer-font-mono)',
            fontSize: 'var(--reviewer-text-sm)',
            color: 'var(--reviewer-text-secondary)',
            marginBottom: '16px',
          }}
        >
          {t('kc.tokenLead')}
        </p>
        <form onSubmit={handleSaveToken} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <label style={{ fontFamily: 'var(--reviewer-font-mono)', fontSize: 'var(--reviewer-text-sm)' }}>
            {t('kc.tokenLabel')}
            <input
              type="password"
              value={tokenDraft}
              onChange={(event) => setTokenDraft(event.target.value)}
              autoComplete="off"
              data-testid="knowledge-control-token-input"
              style={{
                display: 'block',
                width: '100%',
                marginTop: '8px',
                padding: '10px 12px',
                border: '1px solid var(--reviewer-border)',
                borderRadius: 0,
                background: 'var(--reviewer-bg-primary)',
                color: 'var(--reviewer-text)',
                fontFamily: 'var(--reviewer-font-mono)',
              }}
            />
          </label>
          <Button type="submit" variant="primary" disabled={!tokenDraft.trim()}>
            {t('kc.authenticate')}
          </Button>
        </form>
      </div>
    )
  }

  if (loading && items.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '400px' }}>
        <LoadingSpinner size="lg" text={t('kc.loading')} />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ maxWidth: '600px', margin: '0 auto', padding: '32px' }}>
        <h2 style={{ fontFamily: 'var(--reviewer-font-mono)', color: 'var(--reviewer-danger)' }}>
          {t('kc.failed')}
        </h2>
        <p style={{ fontFamily: 'var(--reviewer-font-mono)', marginBottom: '16px' }}>{error}</p>
        <Button onClick={loadQueue} variant="danger">
          {t('common.retry')}
        </Button>
      </div>
    )
  }

  return (
    <div style={{ padding: '24px' }} data-testid="knowledge-control-page">
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '24px' }}>
        <div>
          <h1
            style={{
              margin: 0,
              fontFamily: 'var(--reviewer-font-mono)',
              fontSize: 'var(--reviewer-text-2xl)',
            }}
          >
            {t('nav.knowledgeControl')}
          </h1>
          <p
            style={{
              marginTop: '8px',
              fontFamily: 'var(--reviewer-font-mono)',
              color: 'var(--reviewer-text-secondary)',
              fontSize: 'var(--reviewer-text-sm)',
            }}
          >
            {t('kc.lead')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button onClick={() => navigate('/knowledge-control/packs')} variant="secondary" size="sm">
            {t('kc.openPacks')}
          </Button>
          <Button onClick={handleClearToken} variant="ghost" size="sm">
            {t('packs.clearToken')}
          </Button>
        </div>
      </div>

      {stats && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: '12px',
            marginBottom: '24px',
          }}
        >
          {[
            [t('kc.statDraft'), stats.pending_proposals, 'draft'],
            [t('kc.statTesting'), stats.pending_approvals, 'testing'],
            [t('kc.statReady'), stats.approved_ready, 'approved'],
            [t('kc.statTotal'), stats.total_rules, ''],
            [t('kc.statPacks'), stats.total_packs, ''],
          ].map(([label, value, filter]) => (
            <button
              key={label}
              type="button"
              onClick={() => updateFilter('status', String(filter))}
              style={{
                textAlign: 'left',
                border: '1px solid var(--reviewer-border)',
                borderRadius: 0,
                padding: '12px',
                background: 'var(--reviewer-bg-primary)',
                cursor: 'pointer',
                fontFamily: 'var(--reviewer-font-mono)',
              }}
            >
              <div style={{ fontSize: '11px', color: 'var(--reviewer-text-tertiary)' }}>{label}</div>
              <div style={{ fontSize: '22px' }}>{value}</div>
            </button>
          ))}
        </div>
      )}

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <input
          type="search"
          placeholder={t('kc.search')}
          value={search}
          onChange={(event) => updateFilter('search', event.target.value)}
          style={{
            flex: '1 1 220px',
            padding: '8px 12px',
            border: '1px solid var(--reviewer-border)',
            borderRadius: 0,
            fontFamily: 'var(--reviewer-font-mono)',
            background: 'var(--reviewer-bg-primary)',
            color: 'var(--reviewer-text)',
          }}
        />
        <select
          value={status}
          onChange={(event) => updateFilter('status', event.target.value)}
          aria-label={t('kc.statusAria')}
          style={{
            padding: '8px 12px',
            border: '1px solid var(--reviewer-border)',
            borderRadius: 0,
            fontFamily: 'var(--reviewer-font-mono)',
            background: 'var(--reviewer-bg-primary)',
            color: 'var(--reviewer-text)',
          }}
        >
          {STATUS_VALUES.map((value) => (
            <option key={value || 'needs'} value={value}>
              {value ? lifecycleLabel(locale, value) : t('life.all')}
            </option>
          ))}
        </select>
      </div>

      {items.length === 0 ? (
        <EmptyState
          title={t('kc.empty')}
          description={search || status ? t('kc.emptyFilter') : t('kc.emptyQueue')}
        />
      ) : (
        <div data-testid="knowledge-control-table" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: 'var(--reviewer-font-mono)' }}>
            <thead>
              <tr>
                {[
                  t('kc.colRule'),
                  t('packs.colStatus'),
                  t('packs.colValidation'),
                  t('findings.severity'),
                  t('packs.colFormat'),
                  t('kc.colActions'),
                ].map((header) => (
                  <th
                    key={header}
                    style={{
                      textAlign: 'left',
                      borderBottom: '1px solid var(--reviewer-border)',
                      padding: '10px 8px',
                      fontSize: '11px',
                      color: 'var(--reviewer-text-tertiary)',
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr
                  key={item.id}
                  onClick={() => navigate(`/knowledge-control/${item.id}`)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      navigate(`/knowledge-control/${item.id}`)
                    }
                  }}
                  tabIndex={0}
                  style={{ cursor: 'pointer' }}
                >
                  <td style={{ padding: '12px 8px' }}>
                    <div>{item.title}</div>
                    <div style={{ fontSize: '11px', color: 'var(--reviewer-text-tertiary)' }}>
                      {item.rule_id}@{item.version}
                    </div>
                  </td>
                  <td style={{ padding: '12px 8px' }}>
                    <Badge variant={statusTone(item.status)}>{lifecycleLabel(locale, item.status)}</Badge>
                  </td>
                  <td style={{ padding: '12px 8px' }}>{validationLabel(locale, item.validation_status)}</td>
                  <td style={{ padding: '12px 8px' }}>{severityLabel(locale, item.severity)}</td>
                  <td style={{ padding: '12px 8px' }}>{formatLabel(locale, item.format_type)}</td>
                  <td style={{ padding: '12px 8px', fontSize: '11px' }}>
                    {item.legal_actions.map((action) => actionLabel(locale, action)).join(', ') || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
          <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => goToPage(page - 1)}>
            {t('common.previous')}
          </Button>
          <span style={{ fontFamily: 'var(--reviewer-font-mono)', alignSelf: 'center' }}>
            {t('packs.page', { page, total: totalPages, count: totalCount })}
          </span>
          <Button
            variant="secondary"
            size="sm"
            disabled={page >= totalPages}
            onClick={() => goToPage(page + 1)}
          >
            {t('common.next')}
          </Button>
        </div>
      )}
    </div>
  )
}
